/**
 * Digital Role - Cast Pillar
 *
 * Normaliza input sujo para número, com detecção automática de unidade.
 *
 * @example
 * import { castDigital, castDigitalStrict, detectDigitalUnit } from '@attrx/role-morphic/digital/cast';
 *
 * castDigital('1 MB');                     // 1000000 (converted to bytes)
 * castDigital('1 MB', 'megabyte');         // 1 (kept as megabytes)
 * castDigitalStrict('100');                // 100 (no conversion)
 * detectDigitalUnit('2 GiB');              // 'gibibyte'
 */

import { DIGITAL_UNITS, DIGITAL_ALIASES, type DigitalUnit } from "./constants";
import { convertDigital, DIGITAL_BASE } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, com conversão opcional para unidade alvo
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo (default: byte)
 * @returns Número ou null se não puder converter
 *
 * @example
 * castDigital('1 MB');             // 1000000 (bytes)
 * castDigital('1 MB', 'megabyte'); // 1
 * castDigital('100');              // 100
 * castDigital(100);                // 100
 * castDigital('invalid');          // null
 */
export function castDigital(
  input: unknown,
  targetVariant: DigitalUnit = DIGITAL_BASE,
): number | null {
  // Already a number
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String parsing
  if (typeof input === "string") {
    const parsed = parseDigitalString(input);
    if (parsed === null) {
      return null;
    }

    // If unit was detected and differs from target, convert
    if (parsed.unit && parsed.unit !== targetVariant) {
      return convertDigital(parsed.unit, targetVariant, parsed.value);
    }

    return parsed.value;
  }

  return null;
}

/**
 * Faz cast sem conversão (apenas parseia o número)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castDigitalStrict('100 MB');  // 100 (ignora a unidade)
 * castDigitalStrict('100');     // 100
 */
export function castDigitalStrict(input: unknown): number | null {
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input === "string") {
    const parsed = parseDigitalString(input);
    return parsed?.value ?? null;
  }

  return null;
}

/**
 * Versão safe de castDigital que retorna Result
 */
export function tryCastDigital(
  input: unknown,
  targetVariant: DigitalUnit = DIGITAL_BASE,
): Result<number> {
  const result = castDigital(input, targetVariant);
  if (result === null) {
    return { ok: false, error: `Cannot cast "${String(input)}" to digital storage` };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECT UNIT
// =============================================================================

/**
 * Detecta a unidade de armazenamento digital de uma string
 *
 * @param input - String com valor e unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectDigitalUnit('100 MB');   // 'megabyte'
 * detectDigitalUnit('2 GiB');    // 'gibibyte'
 * detectDigitalUnit('100');      // null
 */
export function detectDigitalUnit(input: string): DigitalUnit | null {
  const trimmed = input.trim();

  // Extract unit part (after the number)
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);
  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  if (!unitStr) {
    return null;
  }

  const lowerUnitStr = unitStr.toLowerCase();

  // Check direct unit names
  if (lowerUnitStr in DIGITAL_UNITS) {
    return lowerUnitStr as DigitalUnit;
  }

  // Check aliases (case-sensitive for IEC units like KiB, MiB)
  if (unitStr in DIGITAL_ALIASES) {
    return DIGITAL_ALIASES[unitStr] as DigitalUnit;
  }
  if (lowerUnitStr in DIGITAL_ALIASES) {
    return DIGITAL_ALIASES[lowerUnitStr] as DigitalUnit;
  }

  // Check symbols
  for (const [unit, config] of Object.entries(DIGITAL_UNITS)) {
    if (config.symbol === unitStr || config.symbol.toLowerCase() === lowerUnitStr) {
      return unit as DigitalUnit;
    }
  }

  return null;
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

interface ParsedDigital {
  value: number;
  unit: DigitalUnit | null;
}

/**
 * Parseia string de armazenamento digital extraindo valor e unidade
 */
function parseDigitalString(input: string): ParsedDigital | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  // Try to extract number and unit
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const numStr = match[1].replace(/\s/g, "").replace(",", ".");
  const value = parseFloat(numStr);

  if (isNaN(value) || !Number.isFinite(value)) {
    return null;
  }

  const unitStr = match[2]?.trim() || "";
  const lowerUnitStr = unitStr.toLowerCase();
  let unit: DigitalUnit | null = null;

  if (unitStr) {
    // Check aliases (case-sensitive for IEC)
    if (unitStr in DIGITAL_ALIASES) {
      unit = DIGITAL_ALIASES[unitStr] as DigitalUnit;
    } else if (lowerUnitStr in DIGITAL_ALIASES) {
      unit = DIGITAL_ALIASES[lowerUnitStr] as DigitalUnit;
    }
    // Check direct unit names
    else if (lowerUnitStr in DIGITAL_UNITS) {
      unit = lowerUnitStr as DigitalUnit;
    }
    // Check symbols
    else {
      for (const [u, config] of Object.entries(DIGITAL_UNITS)) {
        if (config.symbol === unitStr || config.symbol.toLowerCase() === lowerUnitStr) {
          unit = u as DigitalUnit;
          break;
        }
      }
    }
  }

  return { value, unit };
}
